import React from "react";
import { Box, Typography } from "@mui/material";
import { Storefront, Analytics, Loyalty } from "@mui/icons-material";
import MallInHandLogo from "../MallInHandLogo";

interface FeatureCardProps {
  icon: React.ReactElement;
  title: string;
  description: string;
}

/**
 * FeatureCard Component
 *
 * Individual feature showcase card with icon, title, and description.
 * Used in BrandingPanel to display platform features.
 */
const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, md: 2 },
        p: { xs: 1.75, sm: 2, md: 2.5 },
        borderRadius: 2,
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        backdropFilter: "blur(20px)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.12)",
          transform: "translateY(-4px) scale(1.02)",
          borderColor: "rgba(255, 215, 0, 0.4)",
          boxShadow:
            "0 12px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 215, 0, 0.1)",
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 1, md: 1.25 },
          borderRadius: 1.5,
          background:
            "linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.05) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: { xs: 38, md: 44 },
          minHeight: { xs: 38, md: 44 },
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: { xs: 20, md: 24 }, color: "#FFD700" },
        })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "white",
            mb: 0.25,
            fontSize: { xs: "0.85rem", md: "0.95rem" },
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255, 255, 255, 0.75)",
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>
      </Box>
    </Box>
  );
};

interface BrandingPanelProps {
  /** Optional custom features array. If not provided, uses default features. */
  features?: Array<{
    icon: React.ReactElement;
    title: string;
    description: string;
  }>;
  /** Optional custom brand name */
  brandName?: string;
  /** Optional custom tagline */
  tagline?: string;
  /** Optional custom description */
  description?: string;
}

/**
 * BrandingPanel Component
 *
 * Left panel of login screen with branding, features, and footer.
 * Displays company logo, tagline, feature showcase, and copyright.
 *
 * Reusable for: Login, Signup, Forgot Password screens.
 */
const BrandingPanel: React.FC<BrandingPanelProps> = ({
  features,
  brandName = "MallInHand",
  tagline = "Complete Digital Mall Experience",
  description = "Empower your mall with comprehensive digital solutions - from customer engagement and store management to real-time analytics and seamless payments.",
}) => {
  const defaultFeatures = [
    {
      icon: <Storefront />,
      title: "Digital Mall Hub",
      description:
        "Complete mall ecosystem with stores, offers, events & parking",
    },
    {
      icon: <Analytics />,
      title: "Real-time Insights",
      description: "Monitor footfall, revenue, and customer behavior analytics",
    },
    {
      icon: <Loyalty />,
      title: "Customer Engagement",
      description: "WhatsApp notifications, offers, and loyalty programs",
    },
  ];

  const displayFeatures = features || defaultFeatures;

  return (
    <Box
      sx={{
        flex: 1,
        background:
          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #1a237e 100%)",
        display: "flex",
        flexDirection: "column",
        p: { xs: 3, sm: 4, md: 5, lg: 7 },
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Gradient Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 215, 0, 0.06) 0%, transparent 50%)",
          animation: "pulse 6s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.4 },
            "50%": { opacity: 0.7 },
          },
        }}
      />

      {/* Decorative circles */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 215, 0, 0.15) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%)",
          filter: "blur(80px)",
        }}
      />

      {/* Content Container */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Brand Logo Section */}
        <Box sx={{ mb: { xs: 3, sm: 4, md: 5, lg: 6 } }}>
          <Box sx={{ mb: { xs: 2, md: 3 } }}>
            <MallInHandLogo
              variant="full"
              width={{ xs: 180, sm: 200, md: 220, lg: 260 }}
              color="white"
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: { xs: 1.5, md: 2 },
              color: "#FFD700",
              fontSize: {
                xs: "1rem",
                sm: "1.1rem",
                md: "1.15rem",
                lg: "1.25rem",
              },
              letterSpacing: "0.5px",
            }}
          >
            {tagline}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.85)",
              lineHeight: 1.7,
              maxWidth: 440,
              fontSize: {
                xs: "0.85rem",
                sm: "0.875rem",
                md: "0.9rem",
                lg: "0.9375rem",
              },
            }}
          >
            {description}
          </Typography>
        </Box>

        {/* Features Grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, md: 2 },
            mb: "auto",
          }}
        >
          {displayFeatures.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: { xs: 3, sm: 4, md: 5, lg: 6 },
            pt: { xs: 2, md: 3 },
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.6)",
              textAlign: "center",
              fontSize: { xs: "0.75rem", md: "0.8125rem" },
              fontWeight: 500,
            }}
          >
            © 2026 SimplyTechiefy. MallInHand - Transforming Mall Experiences
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(BrandingPanel);
